import {SERVICE_PEER_ID, VERIFICATION_CODES_BOT_ID} from '@appManagers/constants';
import rootScope from '@lib/rootScope';
import CorpConfig from './config';
import {isCorpServiceMessageHidden, type CorpHideableMessage} from './serviceMessageHide';
import {ControlledAction, ControlledPolicy} from './types';

/**
 * 主线程 UI 策略缓存：策略统一由 Worker 的 corpGuard 拉取并通过 rootScope 广播。
 * 主线程不再独立轮询，避免后台节流造成 UI 与 Worker 执行策略不一致。
 *
 * 策略语义：默认全部允许（黑名单）；仅管理员显式禁止的操作才拦截 / 隐藏入口，
 * 策略未就绪时放行。审查只读模式（isAuditMode）例外：一切写 / 会暴露行为的操作均禁止。
 */

let policy: ControlledPolicy | null = null;
let started = false;
let hideServiceMessages = false;
let hideServiceMessagesSince: number | undefined;

function requestWorkerSnapshot() {
  const managers = rootScope.managers;
  if(!managers?.apiManager) return;
  Promise.resolve(managers.apiManager.corpRefreshPolicy()).catch(() => {});
}

/** 策略变化后重算 777000/验证码会话预览，使列表与气泡过滤一致 */
function refreshServiceDialogPreviews() {
  const managers = rootScope.managers;
  if(!managers?.dialogsStorage) return;
  for(const peerId of [SERVICE_PEER_ID, VERIFICATION_CODES_BOT_ID]) {
    Promise.resolve(managers.dialogsStorage.getDialogOnly(peerId))
    .then((dialog: any) => {
      if(!dialog) return;
      return Promise.resolve(managers.dialogsStorage.setDialogToState(dialog)).then(() => {
        const map = new Map();
        map.set(peerId, {dialog});
        rootScope.dispatchEvent('dialogs_multiupdate', map);
      });
    })
    .catch(() => {});
  }
}

/** 某受控操作是否被允许（默认允许，仅显式禁止才拦） */
export function isActionAllowed(action: ControlledAction): boolean {
  if(!CorpConfig.enabled) return true;
  if(CorpConfig.isAuditMode) return true; // 审查实例：正常走逻辑，不隐藏/不禁
  if(!policy) return true; // 默认允许：策略未就绪时放行
  return policy[action] !== false; // 仅显式禁止才拦
}

/** 某 UI 入口是否应隐藏（与对应受控操作同名） */
export function isUiHidden(key: ControlledAction): boolean {
  if(!CorpConfig.enabled) return false; // 非企业模式不隐藏任何入口
  return !isActionAllowed(key);
}

/** 是否开启服务号消息过滤（开关本身；细粒度按条判断用 shouldHideCorpServiceMessage） */
export function shouldHideServiceMessages(): boolean {
  if(!CorpConfig.enabled || CorpConfig.isAuditMode) return false; // 审查实例可见全部
  return hideServiceMessages;
}

/** 过滤水位线（unix 秒）；未开启返回 undefined */
export function getHideServiceMessagesSince(): number | undefined {
  if(!CorpConfig.enabled || CorpConfig.isAuditMode) return undefined;
  if(!hideServiceMessages) return undefined;
  return hideServiceMessagesSince;
}

/** 主线程：该条入站系统消息是否应按水位线隐藏 */
export function shouldHideCorpServiceMessage(message: CorpHideableMessage | undefined | null): boolean {
  return isCorpServiceMessageHidden(message, getHideServiceMessagesSince());
}

export default function initCorpPolicy() {
  if(!CorpConfig.enabled || started) return;
  started = true;

  rootScope.addEventListener('corp_policy_update', (snapshot) => {
    const prevSince = hideServiceMessagesSince;
    const prevHide = hideServiceMessages;
    policy = snapshot.policy || {};
    hideServiceMessages = snapshot.hideServiceMessages;
    hideServiceMessagesSince = snapshot.hideServiceMessages && snapshot.hideServiceMessagesSince ?
      Number(snapshot.hideServiceMessagesSince) || undefined :
      undefined;
    if(prevHide !== hideServiceMessages || prevSince !== hideServiceMessagesSince) {
      refreshServiceDialogPreviews();
    }
  });

  // 账号切换/重新登录时先清空旧账号 UI 缓存，等待 Worker 推送新快照。
  rootScope.addEventListener('user_auth', () => {
    policy = null;
    hideServiceMessages = false;
    hideServiceMessagesSince = undefined;
    requestWorkerSnapshot();
  });

  requestWorkerSnapshot();
}
